import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Moon, Sun } from 'lucide-react';
import { BRAND, NAV_LINKS } from '@/lib/site';
import { Button } from '@/components/ui/button';
import { useDarkMode } from '@/hooks/useDarkMode';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { isDark, toggle } = useDarkMode();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? 'py-2' : 'py-4'
      }`}
    >
      <div className="container-x">
        <nav
          className={`flex items-center justify-between rounded-full px-3 py-2 transition-all duration-500 ${
            scrolled
              ? 'glass-strong'
              : 'bg-transparent border border-transparent'
          }`}
        >
          {/* Logo */}
          <a
            href="#home"
            data-testid="navbar-logo"
            className="group flex items-center gap-3 pl-2 pr-3"
          >
            <div className="relative">
              <div className="absolute inset-0 -z-10 rounded-full bg-saffron-glow scale-150 opacity-70 group-hover:opacity-100 transition-opacity" />
              <img
                src={BRAND.logo}
                alt="ADI Foundation"
                className="h-10 w-10 object-contain mix-blend-multiply"
              />
            </div>
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display text-[15px] font-bold tracking-wide text-maroon-700 dark:text-saffron-200">
                ADI FOUNDATION
              </span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-saffron-600 dark:text-saffron-400/80">
                Nar Sewa Narayan Sewa
              </span>
            </div>
          </a>

          {/* Desktop nav */}
          <ul className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <li key={l.label}>
                <a
                  href={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className="rounded-full px-3 py-2 text-sm font-medium text-foreground/80 hover:text-saffron-600 dark:hover:text-saffron-300 transition-colors"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>

          {/* Right cluster */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Toggle dark mode"
              data-testid="theme-toggle"
              onClick={toggle}
              className="relative grid h-10 w-10 place-items-center rounded-full border border-border/60 bg-background/60 hover:border-saffron-500/50 transition"
            >
              <AnimatePresence mode="wait" initial={false}>
                {isDark ? (
                  <motion.span
                    key="sun"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="absolute"
                  >
                    <Sun className="h-4 w-4 text-saffron-300" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="moon"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="absolute"
                  >
                    <Moon className="h-4 w-4 text-maroon-700" />
                  </motion.span>
                )}
              </AnimatePresence>
            </button>

            <Button
              asChild
              data-testid="nav-donate-btn"
              className="hidden sm:inline-flex rounded-full bg-saffron-500 hover:bg-saffron-600 text-white px-5 shadow-[0_8px_24px_-8px_rgba(255,103,31,0.6)]"
            >
              <a href="#donate">Donate</a>
            </Button>

            <button
              type="button"
              aria-label="Open menu"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              className="lg:hidden grid h-10 w-10 place-items-center rounded-full border border-border/60"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] lg:hidden"
            data-testid="mobile-menu"
          >
            <div
              className="absolute inset-0 bg-ink/60 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 260, damping: 30 }}
              className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-background border-l border-border p-6 flex flex-col"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img src={BRAND.logo} alt="" className="h-10 w-10 mix-blend-multiply" />
                  <div className="leading-tight">
                    <p className="font-display font-bold text-maroon-700 dark:text-saffron-200">
                      ADI FOUNDATION
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-saffron-600">
                      Nar Sewa Narayan Sewa
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="mobile-menu-close"
                  onClick={() => setOpen(false)}
                  className="grid h-10 w-10 place-items-center rounded-full border border-border"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <ul className="mt-8 flex flex-col gap-1">
                {NAV_LINKS.map((l, i) => (
                  <motion.li
                    key={l.label}
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.04 * i }}
                  >
                    <a
                      href={l.href}
                      data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                      onClick={() => setOpen(false)}
                      className="block rounded-2xl px-4 py-3 font-display text-2xl text-foreground/90 hover:bg-saffron-50 dark:hover:bg-maroon-900/40"
                    >
                      {l.label}
                    </a>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-auto space-y-3">
                <Button
                  asChild
                  data-testid="mobile-donate-btn"
                  className="w-full rounded-full bg-saffron-500 hover:bg-saffron-600 text-white py-6 text-base"
                >
                  <a href="#donate" onClick={() => setOpen(false)}>
                    Donate Now
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="w-full rounded-full border-maroon-600 text-maroon-700 dark:text-saffron-200 dark:border-saffron-300 py-6 text-base"
                >
                  <a href="#volunteer" onClick={() => setOpen(false)}>
                    Become a Volunteer
                  </a>
                </Button>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
